// R210 — **THE ROOM INVENTED A REASON FOR A FAILURE NOBODY EXPLAINED, AND THE REASON IT INVENTED
// WAS A CLAIM ABOUT HER PHOTOGRAPH.**
//
// Three rooms upload a thing and wait for a reading — Palmistry, Dream, and the Tarot analyzer —
// and all three hand-typed the SAME four-way answer inside a hook-holding component, which is why
// nothing in this repo had ever run any of it (roadmap 7's own currency: the thing that had to move
// is the thing nothing could reach). Read side by side, two of them agreed with their own server
// and one did not:
//
//     dream    non-ok -> data.message || "Your dream reading could not be prepared right now."
//     tarot    non-ok -> data.message || "Your reading could not be prepared right now…"
//     PALM     non-ok -> data.message || "I could not read that palm clearly. A brighter, closer
//                                         photo of your open hand will help."
//
// **`palm.failed` — the honest house-fault sentence — was DECLARED, TRANSLATED, AND REACHABLE ONLY
// FROM THE `catch`.** Every response that was not this handler's — a 502 from the edge mid-redeploy
// (ITEM 13b exists because this site is redeployed under a live crowd), a proxy timeout page, any
// body that is not JSON, since the unwrap is `.json().catch(() => ({}))` — arrived with no
// `message`, fell to that fallback, and told her that her open hand was unclear.
//
// **IT IS THE WRONG INSTRUCTION AS WELL AS A WRONG SENTENCE** (R99's own wording, one drawer over,
// and R60/R64/R70 before it): no brighter photograph will fix a 502. **AND THIS ONE BILLS HER FOR
// IT** — she is told to retake and resubmit, which buys a second paid generation for a fault that
// was never hers.
//
// **AND THE SENTENCE WAS A SECOND COPY OF ONE THE SERVER ALREADY COMPOSES, BYTE FOR BYTE**
// (`handlePalmReading`'s own `built.error || !built.report` arm, R28/R29). The server is the only
// party that has ever looked at the palm; the client cannot know that and never can. So the client
// keeps NO opinion about her input at all — this module composes no member-facing sentence anywhere
// in it, which is a property a gate can read rather than a promise.
//
// **FIVE OUTCOMES, NEVER TWO (R18), AND THE ROUND IS THE LAST TWO.** `explained` is the server
// naming a reason; `unexplained` is nobody naming one. Collapsing them is the defect above, and it
// renders perfectly.
//
// **THE ORDER IS LOAD-BEARING AND IS HELD AS AN ORDER, NOT AS MEMBERSHIP.**
//   • `sign-in` outranks everything: a 401 read as a failure tells a signed-out member the house
//     broke, and hides the one door she can walk through.
//   • `confirm` outranks the failure arm. The Tarot analyzer's "which cards did you lay?" question
//     arrives as a 200 carrying no `cards` — so asked one line lower it becomes a failure sentence,
//     and a legitimate question to the member reads as the reading dying.
//
// PRESENCE, NEVER TRUTHINESS (R53): a 200 that says `ok: true` and carries no reading did not
// answer this question. And it is `ok === false` rather than `!ok`, because the Tarot payload
// carries a reading and NO `ok` key at all — `!d.ok` would refuse the one room that shipped first.
//
// This module imports NOTHING (the entry requirement for a `scripts/`-loadable law), so
// `scripts/r210-reading-answer.mjs` RUNS every answer these three routes and their edge can give.

export type ReadingAnswer<T> =
  | { kind: "delivered"; body: T }
  | { kind: "sign-in" }
  | { kind: "confirm"; message: string }
  | { kind: "explained"; message: string }
  | { kind: "unexplained" };

// A request that never came back. Named rather than left to a bare `catch` composing its own
// sentence: from the member's side a thrown fetch and a 502 carrying an HTML page are ONE fact, and
// a room that answers them with two different sentences has two answers to one question (R28/R29 —
// the Tarot analyzer had exactly that, `reading.failed` in its catch against `tarot.analyze.failed`
// eight lines up).
export const READING_UNREACHABLE: ReadingAnswer<never> = { kind: "unexplained" };

const asRecord = (data: unknown): Record<string, unknown> =>
  data && typeof data === "object" ? (data as Record<string, unknown>) : {};

// The server's own words, or nothing. A blank or non-string `message` is NOT the server explaining
// itself — it is a payload with no sentence in it — and reading it as one would print an empty
// status line where the room's own sentence belongs.
const explained = (d: Record<string, unknown>): string =>
  typeof d.message === "string" && d.message.trim() ? d.message : "";

export function readingAnswer<T>(
  status: number,
  data: unknown,
  // WHAT COUNTS AS A DELIVERED READING IS THE ROOM'S OWN QUESTION and is never guessed here: the
  // palm and the dream carry `report`, the analyzer carries `cards` and hands back the whole
  // payload. A shared law that picked one of those would silently refuse the other two.
  deliver: (data: Record<string, unknown>) => T | null | undefined,
): ReadingAnswer<T> {
  const d = asRecord(data);
  if (status === 401) return { kind: "sign-in" };
  if (d.confirm) return { kind: "confirm", message: explained(d) };
  const transportOk = status >= 200 && status < 300;
  const body = transportOk && d.ok !== false ? deliver(d) : null;
  if (body !== null && body !== undefined) return { kind: "delivered", body };
  const message = explained(d);
  return message ? { kind: "explained", message } : { kind: "unexplained" };
}

// WHAT THE ROOM PRINTS UNDER THE FORM. A delivered reading says NOTHING — the reading appearing IS
// the answer — and the two sentences a room supplies are its OWN: the door it can walk through, and
// the one thing that is honestly true when nobody explained anything.
//
// `failed` is the whole of the room's opinion about a failure, so there is exactly one such
// sentence per room rather than three spellings of it, and it can only ever be about the HOUSE.
export function readingStatusLine(
  answer: ReadingAnswer<unknown>,
  copy: { signIn: string; failed: string },
): string {
  switch (answer.kind) {
    case "delivered":
      return "";
    case "sign-in":
      return copy.signIn;
    // The server asked her something. Its words or nothing — the room may not answer a question on
    // her behalf, and `data.message || ""` is what the analyzer already did.
    case "confirm":
      return answer.message;
    case "explained":
      return answer.message;
    default:
      return copy.failed;
  }
}
